(() => {
  if (window.drboProductMediaModesLoaded) return;
  window.drboProductMediaModesLoaded = true;

  const ROOT_SELECTOR = '[data-product-media-modes-root]';

  const parseSources = (value) => {
    if (!value) return [];
    try {
      const sources = JSON.parse(value);
      return Array.isArray(sources) ? sources.filter(Boolean) : [];
    } catch (_error) {
      return value.split('|').filter(Boolean);
    }
  };

  const configurePlaylist = (video) => {
    if (!(video instanceof HTMLVideoElement)) return [];
    const sources = parseSources(video.dataset.videoSources || '');
    if (sources.length === 0) return [];
    if (video.dataset.drboPlaylistReady === 'true') return sources;

    video.dataset.drboPlaylistReady = 'true';
    video.dataset.drboPlaylistIndex = String(Math.max(0, sources.indexOf(video.currentSrc || video.src)));
    video.addEventListener('ended', () => {
      const playlistIndex = Number(video.dataset.drboPlaylistIndex || 0) + 1;
      if (!sources[playlistIndex]) {
        video.dataset.drboPlaylistIndex = '0';
        video.src = sources[0];
        video.load();
        return;
      }
      const wasMuted = video.muted;
      const volume = video.volume;
      const playbackRate = video.playbackRate;
      video.dataset.drboPlaylistIndex = String(playlistIndex);
      video.addEventListener('loadedmetadata', () => {
        video.muted = wasMuted;
        video.volume = volume;
        video.playbackRate = playbackRate;
        const playPromise = video.play();
        if (playPromise?.catch) playPromise.catch(() => {});
      }, { once: true });
      video.src = sources[playlistIndex];
      video.load();
    });
    return sources;
  };

  const hydrateVideo = (video) => {
    if (!(video instanceof HTMLVideoElement)) return false;
    const playlist = configurePlaylist(video);
    if (playlist.length > 0 && video.ended) {
      video.dataset.drboPlaylistIndex = '0';
      video.src = playlist[0];
      video.load();
      return true;
    }
    if (playlist.length > 0 && !video.currentSrc && !video.src) {
      video.src = playlist[0];
      video.load();
      return true;
    }

    let hydrated = false;
    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src || '';
      source.removeAttribute('data-src');
      hydrated = true;
    });
    if (hydrated) video.load();
    return hydrated;
  };

  const initDescriptionVideos = (scope = document) => {
    const videos = scope.querySelectorAll?.('video[data-drbo-description-cdn-video]') || [];
    videos.forEach((video) => {
      const sources = configurePlaylist(video);
      if (sources.length > 0 && !video.currentSrc && !video.src) video.src = sources[0];
    });
  };

  const init = (root) => {
    if (!(root instanceof HTMLElement) || root.dataset.productMediaModesReady === 'true') return;

    const slider = root.querySelector('[data-js-product-media-slider]');
    if (!(slider instanceof HTMLElement)) return;

    root.dataset.productMediaModesReady = 'true';

    const slideForMedia = (mediaId) => {
      if (!mediaId) return null;
      return slider.querySelector(`.js-product-media-item[data-media-id="${CSS.escape(String(mediaId))}"]`);
    };

    const hydrateNativeVideos = (scope) => {
      if (!(scope instanceof Element)) return [];

      const videosToLoad = [];
      scope.querySelectorAll('video').forEach((video) => {
        hydrateVideo(video);
        videosToLoad.push(video);
      });
      return videosToLoad;
    };

    const sourceRegistry = new Map();
    root.querySelectorAll('video[data-original-video-id][data-video-sources]').forEach((video) => {
      const sources = parseSources(video.dataset.videoSources || '');
      if (sources.length > 0) {
        sourceRegistry.set(video.dataset.originalVideoId, {
          poster: video.dataset.videoPoster || '',
          sources
        });
      }
    });

    const migrateZoomExternalVideos = (scope) => {
      if (!(scope instanceof Element)) return;
      scope.querySelectorAll('.js-video-youtube[data-video-id]').forEach((youtubeNode) => {
        const config = sourceRegistry.get(youtubeNode.dataset.videoId);
        const wrapper = youtubeNode.closest('.js-video');
        if (!config || !(wrapper instanceof HTMLElement)) return;
        const video = document.createElement('video');
        video.className = wrapper.className;
        video.classList.remove('product-gallery-video__youtube');
        video.dataset.device = wrapper.dataset.device || 'desktop';
        video.dataset.type = 'html';
        video.dataset.videoSources = JSON.stringify(config.sources);
        if (config.poster) video.poster = config.poster;
        video.preload = 'none';
        video.controls = true;
        video.playsInline = true;
        video.tabIndex = -1;
        video.setAttribute('aria-hidden', 'true');
        wrapper.replaceWith(video);
      });
    };

    migrateZoomExternalVideos(root.querySelector('[data-zoom-modal]'));

    root.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;

      const zoomTrigger = event.target.closest('[data-zoom-media], [data-js-zoom-button]');
      if (zoomTrigger instanceof HTMLElement) {
        const zoomModal = root.querySelector('[data-zoom-modal]');
        migrateZoomExternalVideos(zoomModal);
        hydrateNativeVideos(zoomModal);
      }

      const introButton = event.target.closest('[data-product-media-mode="intro"]');
      if (introButton instanceof HTMLButtonElement) {
        hydrateNativeVideos(slideForMedia(introButton.dataset.mediaId));
        return;
      }

      const playTrigger = event.target.closest('.js-product-gallery-video-placeholder, .js-paused-video');
      if (playTrigger instanceof HTMLElement) {
        const hydratedVideos = hydrateNativeVideos(playTrigger.closest('.js-product-media-item') || playTrigger);
        hydratedVideos.forEach((video) => {
          const playPromise = video.play();
          if (playPromise?.catch) playPromise.catch(() => {});
        });
      }
    }, true);

    const controls = root.querySelector('[data-product-media-modes]');
    if (!(controls instanceof HTMLElement)) return;

    const buttons = Array.from(controls.querySelectorAll('[data-product-media-mode]'));
    const modelPanel = root.querySelector('[data-product-media-model-panel]');
    const introPanel = root.querySelector('[data-product-media-intro-panel]');
    const introFrame = introPanel?.querySelector('[data-product-media-intro-frame]');
    const introVideo = introPanel?.querySelector('[data-product-media-intro-video]');
    const introVideoSources = introVideo instanceof HTMLVideoElement
      ? parseSources(introVideo.dataset.videoSources || '')
      : [];
    let introVideoIndex = 0;
    const photoButton = buttons.find((button) => button.dataset.productMediaMode === 'photos');
    let lastPhotoMediaId = photoButton?.dataset.mediaId || '';

    const activeSlide = () => slider.querySelector('.js-product-media-item.swiper-slide-active')
      || slider.querySelector('.js-product-media-item:not(.is-hidden)');

    const setButtonState = (mode) => {
      root.dataset.productMediaActiveMode = mode;
      buttons.forEach((button) => {
        const active = button.dataset.productMediaMode === mode;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    };

    const pauseMedia = (exceptSlide = null) => {
      slider.querySelectorAll('video').forEach((video) => {
        if (!exceptSlide || !exceptSlide.contains(video)) video.pause();
      });

      if (window.themeCore?.EventBus?.emit) {
        window.themeCore.EventBus.emit('product-slider:slide-change');
      }
    };

    const goToSlide = (slide, smooth = true) => {
      if (!(slide instanceof HTMLElement)) return false;

      const index = Number(slide.dataset.slideIndex);
      if (slider.swiper && Number.isFinite(index)) {
        slider.swiper.slideTo(index, smooth ? 220 : 0);
      } else {
        slide.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'nearest' });
      }
      return true;
    };

    const showModel = () => {
      if (!(modelPanel instanceof HTMLElement)) return false;
      hideIntro();
      pauseMedia();
      modelPanel.hidden = false;
      setButtonState('model');
      return true;
    };

    const hideModel = () => {
      if (modelPanel instanceof HTMLElement) modelPanel.hidden = true;
    };

    const hideIntro = () => {
      if (!(introPanel instanceof HTMLElement)) return;
      introPanel.hidden = true;
      if (introFrame instanceof HTMLIFrameElement) introFrame.removeAttribute('src');
      if (introVideo instanceof HTMLVideoElement) introVideo.pause();
    };

    const showFallbackIntro = () => {
      const hasFrame = introFrame instanceof HTMLIFrameElement;
      const hasVideo = introVideo instanceof HTMLVideoElement && introVideoSources.length > 0;
      if (!(introPanel instanceof HTMLElement) || (!hasFrame && !hasVideo)) return false;
      hideModel();
      pauseMedia();
      introPanel.hidden = false;
      if (hasFrame && (!introFrame.src || introFrame.src === 'about:blank')) {
        introFrame.src = introFrame.dataset.src || '';
      }
      if (hasVideo) {
        if (!introVideo.currentSrc || introVideo.ended) {
          introVideoIndex = 0;
          introVideo.dataset.drboPlaylistIndex = '0';
          introVideo.src = introVideoSources[introVideoIndex];
          introVideo.load();
        }
        const playPromise = introVideo.play();
        if (playPromise?.catch) playPromise.catch(() => {});
      }
      setButtonState('intro');
      return true;
    };

    if (introVideo instanceof HTMLVideoElement) configurePlaylist(introVideo);

    const playIntro = (slide) => {
      if (!(slide instanceof HTMLElement)) return;
      pauseMedia(slide);

      if (slide.dataset.mediaType === 'external_video') {
        const placeholder = slide.querySelector('.js-product-gallery-video-placeholder');
        slide.classList.add('is-paused-video');
        placeholder?.classList.remove('is-hidden');
        return;
      }

      window.requestAnimationFrame(() => {
        const playTarget = slide.querySelector('.js-product-gallery-video-placeholder')
          || slide.querySelector('.js-paused-video');
        if (playTarget instanceof HTMLElement) {
          playTarget.click();
          return;
        }

        const nativeVideo = slide.querySelector('video');
        if (nativeVideo instanceof HTMLVideoElement) {
          const playPromise = nativeVideo.play();
          if (playPromise?.catch) playPromise.catch(() => {});
        }
      });
    };

    const showPhotos = (smooth = true) => {
      hideModel();
      hideIntro();
      const configOverlay = slider.querySelector('[data-drbo-config-media-swap].is-ready:not([hidden])');
      const target = configOverlay?.closest('.js-product-media-item')
        || slideForMedia(lastPhotoMediaId)
        || slider.querySelector('.js-product-media-item[data-media-type="image"]');
      goToSlide(target, smooth);
      pauseMedia();
      setButtonState('photos');
    };

    const showIntro = (button) => {
      if (button.dataset.fallbackIntro === 'true' && showFallbackIntro()) return;
      hideModel();
      hideIntro();
      const target = slideForMedia(button.dataset.mediaId);
      if (!goToSlide(target)) return;
      setButtonState('intro');
      playIntro(target);
    };

    controls.addEventListener('click', (event) => {
      const button = event.target.closest('[data-product-media-mode]');
      if (!(button instanceof HTMLButtonElement)) return;

      const mode = button.dataset.productMediaMode;
      if (mode === 'photos') showPhotos();
      if (mode === 'intro') showIntro(button);
      if (mode === 'model') showModel();
    });

    const syncFromSlider = () => {
      if (root.dataset.productMediaActiveMode === 'model') return;
      if (root.dataset.productMediaActiveMode === 'intro' && introPanel instanceof HTMLElement && !introPanel.hidden) return;
      hideIntro();
      const slide = activeSlide();
      if (!(slide instanceof HTMLElement)) return;

      const type = slide.dataset.mediaType;
      if (type === 'image') {
        lastPhotoMediaId = slide.dataset.mediaId || lastPhotoMediaId;
        if (photoButton) photoButton.dataset.mediaId = lastPhotoMediaId;
        setButtonState('photos');
      } else if (type === 'video' || type === 'external_video') {
        setButtonState('intro');
      } else if (type === 'model') {
        showModel();
      }
    };

    const slideObserver = new MutationObserver(syncFromSlider);
    slider.querySelectorAll('.js-product-media-item').forEach((slide) => {
      slideObserver.observe(slide, { attributes: true, attributeFilter: ['class'] });
    });

    const watchConfigOverlay = (configOverlay) => {
      if (!(configOverlay instanceof HTMLElement) || configOverlay.dataset.productMediaModesObserved === 'true') return;
      configOverlay.dataset.productMediaModesObserved = 'true';
      const configObserver = new MutationObserver(() => {
        if (configOverlay.classList.contains('is-ready') && !configOverlay.hidden) showPhotos(false);
      });
      configObserver.observe(configOverlay, { attributes: true, attributeFilter: ['class', 'hidden'] });
    };

    watchConfigOverlay(slider.querySelector('[data-drbo-config-media-swap]'));
    const configInsertionObserver = new MutationObserver(() => {
      watchConfigOverlay(slider.querySelector('[data-drbo-config-media-swap]'));
    });
    configInsertionObserver.observe(slider, { childList: true, subtree: true });

    root.closest('[data-section-type="product"]')?.addEventListener('click', (event) => {
      if (!event.target.closest('[data-config-item], [data-config-accessory], [data-config-service], [data-config-gift], [data-config-learnmore-toggle]')) return;
      window.setTimeout(() => showPhotos(false), 0);
    });

    syncFromSlider();
  };

  const initAll = (scope = document) => {
    initDescriptionVideos(scope);
    scope.querySelectorAll(ROOT_SELECTOR).forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
